/*
 * Назначение: блок «Опасная зона» во вкладке «Общие» настроек проекта.
 * Функционал: удаление проекта целиком (каскад в БД). Доступ — только для
 * роли owner. Подтверждение через ввод slug, чтобы исключить случайный клик.
 */

import { useState } from "react";
import { AlertCircle, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { projectsApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import { useAuth } from "../../components/auth/auth-provider";
import { useProject } from "../../components/project/project-provider";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import type { ProjectDetails } from "../../../lib/types";

interface DangerZoneProps {
  project: ProjectDetails;
}

export function DangerZone({ project }: DangerZoneProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { setCurrent } = useProject();

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isOwner = user?.role === "owner";

  const deleteMutation = useMutation({
    mutationFn: () => projectsApi.remove(project.slug),
    onSuccess: async () => {
      toast.success(`Проект «${project.name}» удалён`);
      queryClient.removeQueries();
      try {
        const list = await projectsApi.list();
        if (list.length > 0) {
          setCurrent(list[0].slug);
        } else {
          setCurrent("");
        }
      } catch {
        setCurrent("");
      }
      setOpen(false);
      navigate("/dashboard", { replace: true });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.detail : "Не удалось удалить проект");
    },
  });

  const handleConfirm = () => {
    setError(null);
    if (confirmText !== project.slug) {
      setError(`Введи код проекта «${project.slug}» для подтверждения`);
      return;
    }
    deleteMutation.mutate();
  };

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          Опасная зона
        </CardTitle>
        <CardDescription>
          Действия из этого блока — необратимые. Сделай резервную копию, если
          важные данные.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="font-medium">Удалить проект целиком</p>
              <p className="text-sm text-muted-foreground">
                Удалятся: база знаний, диалоги и сообщения, заявки, менеджеры,
                напоминания и документы. Журнал событий и лог моделей —
                сохранятся для аудита.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmText("");
                setError(null);
                setOpen(true);
              }}
              disabled={!isOwner}
              title={isOwner ? undefined : "Только владелец может удалить проект"}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Удалить проект
            </Button>
          </div>
          {!isOwner && (
            <p className="text-xs text-muted-foreground mt-3">
              У тебя роль «{user?.role}» — удалить проект может только
              пользователь с ролью «владелец».
            </p>
          )}
        </div>
      </CardContent>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o && !deleteMutation.isPending) {
            setOpen(false);
            setConfirmText("");
            setError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Удалить проект «{project.name}»?
            </DialogTitle>
            <DialogDescription>
              Это действие необратимо. Все диалоги, заявки, база знаний и
              менеджеры этого проекта будут удалены.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-md border bg-muted/50 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Код проекта:</span>
                <code className="font-mono">{project.slug}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Название:</span>
                <span>{project.name}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dz-confirm">
                Введи код проекта{" "}
                <code className="px-1 rounded bg-muted">{project.slug}</code>,
                чтобы подтвердить удаление
              </Label>
              <Input
                id="dz-confirm"
                autoFocus
                placeholder={project.slug}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={deleteMutation.isPending}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={
                deleteMutation.isPending || confirmText !== project.slug
              }
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Удалить навсегда
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
